/**
 * APX WorkCenter — Platform Costs
 * Dedicated page for the monthly technology stack cost breakdown.
 * Accessible to super_admin only via the sidebar "Platform Costs" nav item.
 */

import DashboardLayout from "@/components/DashboardLayout";

const MONTHLY_STACK = [
  { name: "Google Workspace",   cost: "$66",    category: "Productivity" },
  { name: "Zapier",             cost: "$16",    category: "Automation" },
  { name: "Calendly",           cost: "$20",    category: "Scheduling" },
  { name: "n8n",                cost: "$60",    category: "Automation" },
  { name: "Twilio SMS",         cost: "$20",    category: "Communications" },
  { name: "Manus Subscription", cost: "$250",   category: "AI Operations" },
  { name: "Xero Subscription",  cost: "$1,250", category: "Accounting" },
];

const MONTHLY_TOTAL = "$1,682";

// Category colour map
const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  "CRM":            { bg: "oklch(0.92 0.005 240 / 12%)", text: "oklch(0.82 0.01 240)" },
  "Productivity":   { bg: "oklch(0.92 0.005 240 / 12%)", text: "oklch(0.82 0.01 240)" },
  "Marketing":      { bg: "oklch(0.92 0.005 240 / 12%)", text: "oklch(0.82 0.01 240)" },
  "Automation":     { bg: "oklch(0.92 0.005 240 / 12%)", text: "oklch(0.82 0.01 240)" },
  "Scheduling":     { bg: "oklch(0.92 0.005 240 / 12%)", text: "oklch(0.82 0.01 240)"  },
  "AI Operations":  { bg: "oklch(0.92 0.005 240 / 12%)", text: "oklch(0.82 0.01 240)"  },
  "Accounting":       { bg: "oklch(0.92 0.005 240 / 12%)", text: "oklch(0.82 0.01 240)" },
  "Communications":   { bg: "oklch(0.92 0.005 240 / 12%)", text: "oklch(0.82 0.01 240)" },
};

export default function PlatformCosts() {
  return (
    <DashboardLayout>
      <div style={{ padding: "24px", maxWidth: 760, margin: "0 auto" }}>

        {/* Page header */}
        <div style={{ marginBottom: "28px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
            <span className="apx-phase-badge">Finance</span>
          </div>
          <h1
            className="text-2xl font-bold text-[oklch(0.12_0.04_240)]"
            style={{ fontFamily: "'Playfair Display', serif", marginBottom: "4px" }}
          >
            Platform Costs
          </h1>
          <p className="text-sm text-[oklch(0.08_0.01_240)]">
            Monthly technology stack expenditure for APX.
          </p>
        </div>

        {/* Summary card */}
        <div
          className="apx-card rounded-xl p-5"
          style={{ marginBottom: "20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}
        >
          <div>
            <p className="text-sm text-[oklch(0.08_0.01_240)] uppercase tracking-wider font-medium mb-1">
              Monthly Stack Total
            </p>
            <p
              className="text-4xl font-bold"
              style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.08 0.01 240)" }}
            >
              {MONTHLY_TOTAL}
            </p>
            <p className="text-sm text-[oklch(0.30_0.03_240)] mt-1">
              {MONTHLY_STACK.length} active subscriptions
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            <p className="text-sm text-[oklch(0.08_0.01_240)] uppercase tracking-wider font-medium mb-1">
              Annual Projection
            </p>
            <p
              className="text-2xl font-bold text-[oklch(0.28_0.04_240)]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              ~$20,184
            </p>
            <p className="text-sm text-[oklch(0.30_0.03_240)] mt-1">Based on current monthly rate</p>
          </div>
        </div>

        {/* Line-item breakdown */}
        <div className="apx-card rounded-xl overflow-hidden">
          {/* Table header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 140px 100px",
              padding: "10px 20px",
              background: "oklch(0.93 0.008 240)",
              borderBottom: "1px solid oklch(0.97 0.005 85)",
            }}
          >
            <span className="text-sm font-semibold text-[oklch(0.08_0.01_240)] uppercase tracking-wider">Tool</span>
            <span className="text-sm font-semibold text-[oklch(0.08_0.01_240)] uppercase tracking-wider">Category</span>
            <span className="text-sm font-semibold text-[oklch(0.08_0.01_240)] uppercase tracking-wider text-right">Monthly</span>
          </div>

          {/* Rows */}
          {MONTHLY_STACK.map((item, idx) => {
            const cat = CATEGORY_COLORS[item.category] ?? { bg: "oklch(0.92 0.008 240)", text: "oklch(0.6 0.02 240)" };
            return (
              <div
                key={item.name}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 140px 100px",
                  padding: "13px 20px",
                  borderBottom: idx < MONTHLY_STACK.length - 1 ? "1px solid oklch(0.2 0.04 240 / 60%)" : "none",
                  alignItems: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "oklch(0.92 0.005 240)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; }}
              >
                <span className="text-sm font-medium text-[oklch(0.18_0.04_240)]">{item.name}</span>
                <span>
                  <span
                    className="text-sm font-medium px-2 py-0.5 rounded-full"
                    style={{ background: cat.bg, color: cat.text }}
                  >
                    {item.category}
                  </span>
                </span>
                <span
                  className="text-sm font-mono text-right font-normal"
                  style={{ color: "oklch(0.25 0.02 85)" }}
                >
                  {item.cost}
                </span>
              </div>
            );
          })}

          {/* Total row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 140px 100px",
              padding: "14px 20px",
              background: "oklch(0.90 0.008 240)",
              borderTop: "2px solid oklch(0.78 0.01 240)",
              alignItems: "center",
            }}
          >
            <span className="text-sm font-bold text-[oklch(0.08_0.01_240)]">Monthly Total</span>
            <span />
            <span
              className="text-sm font-mono text-right"
              style={{ color: "oklch(0.08 0.01 240)" }}
            >
              {MONTHLY_TOTAL}
            </span>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-sm text-[oklch(0.28_0.03_240)] mt-4 text-center">
          Costs are manually maintained. Last reviewed June 2026.
        </p>

      </div>
    </DashboardLayout>
  );
}
